import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Bell, BellOff, Trash2 } from "lucide-react";
import {
  deleteSavedSearch,
  listMySavedSearches,
  updateSavedSearch,
} from "@/lib/saved-searches.functions";
import { describeQuery } from "@/lib/saved-search-label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/bevakningar")({
  head: () => ({
    meta: [
      { title: "Bevakningar — Sellavo" },
      {
        name: "description",
        content:
          "Dina sparade sökningar. Få notis så fort en ny annons matchar det du letar efter.",
      },
      { property: "og:title", content: "Bevakningar — Sellavo" },
      {
        property: "og:description",
        content: "Spara en sökning och bli först att se nya träffar.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: WatchesPage,
});

function WatchesPage() {
  const qc = useQueryClient();
  const update = useServerFn(updateSavedSearch);
  const remove = useServerFn(deleteSavedSearch);

  const { data, isLoading } = useQuery({
    queryKey: ["saved-searches"],
    queryFn: () => listMySavedSearches(),
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ["saved-searches"] });

  const patch = useMutation({
    mutationFn: (vars: { id: string; paused?: boolean; notifyChannel?: "push" | "email" | "both" }) =>
      update({ data: vars }),
    onSuccess: refresh,
    onError: () => toast.error("Det gick inte just nu."),
  });

  const del = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Bevakningen är borttagen.");
      refresh();
    },
  });

  const searches = data?.searches ?? [];
  const limit = data?.limit ?? null;

  return (
    <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <header className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Bell className="h-6 w-6 text-primary" /> Bevakningar
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Spara en sökning på annonssidan så hör vi av oss när något nytt matchar.
          {limit !== null && ` Din nivå har plats för ${limit} bevakningar.`}
        </p>
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl border border-border bg-muted/40" />
          ))}
        </div>
      ) : searches.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <p className="text-sm text-muted-foreground">
            Du har inga bevakningar än. Gör en sökning och tryck på «Bevaka sökningen».
          </p>
          <Button asChild className="mt-4">
            <Link to="/annonser">Till alla annonser</Link>
          </Button>
        </div>
      ) : (
        <ul className="space-y-3">
          {searches.map((s: any) => (
            <li
              key={s.id}
              className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="truncate font-medium">{s.label}</p>
                <p className="truncate text-sm text-muted-foreground">{describeQuery(s.query ?? {})}</p>
                {s.paused && (
                  <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <BellOff className="h-3.5 w-3.5" /> Pausad
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Select
                  value={s.notify_channel ?? "both"}
                  onValueChange={(v) =>
                    patch.mutate({ id: s.id, notifyChannel: v as "push" | "email" | "both" })
                  }
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Push + mejl</SelectItem>
                    <SelectItem value="push">Bara push</SelectItem>
                    <SelectItem value="email">Bara mejl</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => patch.mutate({ id: s.id, paused: !s.paused })}
                >
                  {s.paused ? "Aktivera" : "Pausa"}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Ta bort bevakningen"
                  onClick={() => del.mutate(s.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
